import {useEffect, useState} from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {CacheClearJobStatus} from "../../deleteUrls/DeleteUrlsState";
import {extractServerMessage} from "../extractServerMessage";
import {validateCsvFile} from "../validateCsvFile";
import {
  bulkDeleteUrlsState,
  confirmBulkDelete,
  getBulkDeleteJobStatus,
  previewBulkDelete,
  resetToUpload,
  setClientError
} from "../BulkDeleteUrlsSlice";
import {bulkDeleteUrlsModalState, setIsOpened} from "./BulkDeleteUrlsModalSlice";
import BulkDeleteStepper from "./components/BulkDeleteStepper";
import BulkDeleteUpload from "./components/BulkDeleteUpload";
import BulkDeleteReport from "./components/BulkDeleteReport";
import BulkDeleteConfirm from "./components/BulkDeleteConfirm";
import BulkDeleteResult from "./components/BulkDeleteResult";
import "./BulkDeleteUrlsModal.css";

export const OVERSIZE_MESSAGE =
  "The server rejected the upload because it is too large. Split the CSV into smaller batches and retry.";
export const SESSION_EXPIRED_MESSAGE =
  "Your session expired while this window was open, so nothing was deleted. Reload the page and sign in again before retrying.";
export const CONFIRM_FAILED_MESSAGE = "The deletion failed, so nothing was deleted. Please try again in a moment.";
export const CLEAR_ALREADY_RUNNING_MESSAGE =
  "A cache clear is already running for this user — wait for it to finish, then retry. Nothing new was deleted.";
export const POLL_INTERVAL_MS = 10000;

const HASH_MISMATCH_HEADING = "Hash mismatch — deletion refused";
const HASH_MISMATCH_BODY =
  "The file no longer matches the report you previewed, so nothing was deleted. Re-run the preview to get a fresh report, then confirm again.";
const SESSION_EXPIRED_HEADING = "Session expired — deletion refused";
const CLEAR_ALREADY_RUNNING_HEADING = "A cache clear is already running";
const CONFIRM_FAILED_HEADING = "Deletion failed";

const HASH_MISMATCH_STATUS = 400;
const SESSION_EXPIRED_STATUS = 401;
const OVERSIZE_STATUS = 413;
const CLEAR_ALREADY_RUNNING_STATUS = 409;

type ConfirmErrorAlert = {
  readonly heading: string;
  readonly body: string;
};

const confirmErrorAlertFor = (code?: number): ConfirmErrorAlert => {
  if (code === HASH_MISMATCH_STATUS) return {heading: HASH_MISMATCH_HEADING, body: HASH_MISMATCH_BODY};
  if (code === SESSION_EXPIRED_STATUS) return {heading: SESSION_EXPIRED_HEADING, body: SESSION_EXPIRED_MESSAGE};
  if (code === OVERSIZE_STATUS) return {heading: CONFIRM_FAILED_HEADING, body: OVERSIZE_MESSAGE};
  if (code === CLEAR_ALREADY_RUNNING_STATUS) return {heading: CLEAR_ALREADY_RUNNING_HEADING, body: CLEAR_ALREADY_RUNNING_MESSAGE};

  return {heading: CONFIRM_FAILED_HEADING, body: CONFIRM_FAILED_MESSAGE};
};

interface BulkDeleteUrlsModalProps {
  readonly userId: number;
}

const BulkDeleteUrlsModal = (props: BulkDeleteUrlsModalProps) => {
  const {userId} = props;
  const dispatch = useAppDispatch();
  const modalState = useAppSelector(bulkDeleteUrlsModalState);
  const state = useAppSelector(bulkDeleteUrlsState);
  const [file, setFile] = useState<File | null>(null);
  const [confirmStep, setConfirmStep] = useState(false);

  const {preview, previewError, confirmError, clientError, jobStatus, deletedCount} = state;

  useEffect(() => {
    if (jobStatus !== CacheClearJobStatus.processing) return;

    const timer = setInterval(() => {
      dispatch(getBulkDeleteJobStatus({userId}));
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [dispatch, jobStatus, userId]);

  const handleClose = () => {
    dispatch(setIsOpened(false));
    dispatch(resetToUpload());
    setFile(null);
    setConfirmStep(false);
  };

  const selectFile = (selected: File | null) => {
    if (!selected) return;

    const invalid = validateCsvFile(selected);
    if (invalid) {
      setFile(null);
      dispatch(setClientError(invalid));
      return;
    }

    setFile(selected);
    dispatch(setClientError(null));
  };

  const clearFile = () => {
    setFile(null);
    dispatch(setClientError(null));
  };

  const handlePreview = () => {
    if (!file) return;
    dispatch(previewBulkDelete({userId, file}));
  };

  const handleContinueToConfirm = () => setConfirmStep(true);

  const handleBackToReport = () => setConfirmStep(false);

  const handleConfirm = () => {
    if (!file || !preview) return;
    dispatch(confirmBulkDelete({userId, file, contentHash: preview.contentHash}));
  };

  const handleRePreview = () => {
    dispatch(resetToUpload());
    setConfirmStep(false);
  };

  const handleBackToUpload = () => {
    dispatch(resetToUpload());
    setFile(null);
    setConfirmStep(false);
  };

  const previewErrorMessage = previewError
    ? previewError.code === 413
      ? OVERSIZE_MESSAGE
      : extractServerMessage(previewError.message)
    : null;

  const showResult = jobStatus !== CacheClearJobStatus.idle;
  const showConfirm = !showResult && preview !== null && (confirmStep || confirmError !== null);
  const showReport = !showResult && preview !== null && !showConfirm;
  const showConfirmError = showConfirm && confirmError !== null;
  const confirmErrorAlert = showConfirmError ? confirmErrorAlertFor(confirmError?.code) : null;
  const allowRePreview = showConfirmError && confirmError?.code === HASH_MISMATCH_STATUS;

  const currentStep = showResult ? 4 : showConfirm ? 3 : showReport ? 2 : 1;

  return (
    <Modal show={modalState.isOpened} onHide={handleClose} dialogClassName="bulk-delete-modal-dialog">
      <Modal.Header closeButton>
        <Modal.Title>Bulk delete cached URLs</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <BulkDeleteStepper currentStep={currentStep} />
        {showResult && <BulkDeleteResult jobStatus={jobStatus} deletedCount={deletedCount} />}
        {showConfirmError && confirmErrorAlert && (
          <div className="alert alert-danger">
            <h3 className="h5">{confirmErrorAlert.heading}</h3>
            <p className="mb-0">{confirmErrorAlert.body}</p>
          </div>
        )}
        {showConfirm && preview && <BulkDeleteConfirm eligibleCount={preview.eligibleCount} userId={userId} />}
        {showReport && preview && <BulkDeleteReport preview={preview} />}
        {!showResult && !showConfirm && !showReport && (
          <BulkDeleteUpload
            file={file}
            previewErrorMessage={previewErrorMessage}
            clientError={clientError}
            onFileSelected={selectFile}
            onRemoveFile={clearFile}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        {showResult && jobStatus !== CacheClearJobStatus.processing && (
          <Button variant="outline-secondary" onClick={handleBackToUpload}>
            Start another bulk delete
          </Button>
        )}
        {showConfirm && (
          <Button variant="outline-secondary" className="me-auto" onClick={handleBackToReport}>
            ← Back to report
          </Button>
        )}
        {allowRePreview && (
          <Button variant="primary" onClick={handleRePreview}>
            Re-run preview
          </Button>
        )}
        {showConfirm && !showConfirmError && (
          <Button variant="danger" disabled={preview!.eligibleCount === 0} onClick={handleConfirm}>
            Delete {preview!.eligibleCount.toLocaleString()} pages
          </Button>
        )}
        {showReport && (
          <Button variant="outline-secondary" className="me-auto" onClick={handleBackToUpload}>
            Back
          </Button>
        )}
        {showReport && (
          <Button variant="danger" disabled={preview!.eligibleCount === 0} onClick={handleContinueToConfirm}>
            Continue to confirm →
          </Button>
        )}
        {!showResult && !showConfirm && !showReport && (
          <Button variant="primary" disabled={!file} onClick={handlePreview}>
            Run dry-run preview
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkDeleteUrlsModal;
